import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Copy,
  Check,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Database,
  Mic,
  Cpu,
  Box,
  Image,
  Upload,
  Play,
  Trash2,
  History,
  GitBranch,
} from 'lucide-react';
import {
  TritonInstanceDetails,
  TritonModelInfo,
  mockTritonInstanceDetails,
  getMockModelVersionHistory,
} from '@/mock/gpu-instance';
import { cn } from '@/lib/utils';
import {
  useTritonModelStore,
  uploadedModelToTritonModelInfo,
} from '@/store/tritonModelStore';
import UploadModelDialog from '../../components/model-management/UploadModelDialog';
import DeleteModelDialog from '../../components/model-management/DeleteModelDialog';
import VersionHistoryDialog from '../../components/model-management/VersionHistoryDialog';

const getModelIcon = (name: string, backend: string) => {
  if (name.includes('embedding') || name.includes('bge') || name.includes('bert')) {
    return <Database className="h-4 w-4 text-blue-500" />;
  }
  if (name.includes('yolo') || name.includes('detection') || name.includes('resnet') || name.includes('classifier')) {
    return <Image className="h-4 w-4 text-purple-500" />;
  }
  if (name.includes('whisper') || name.includes('speech')) {
    return <Mic className="h-4 w-4 text-cyan-500" />;
  }
  if (backend === 'tensorrt') {
    return <Cpu className="h-4 w-4 text-green-500" />;
  }
  return <Box className="h-4 w-4 text-gray-500" />;
};

const getStatusIcon = (status: TritonModelInfo['status']) => {
  switch (status) {
    case 'ready':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case 'loading':
      return <Loader2 className="h-4 w-4 text-yellow-500 animate-spin" />;
    case 'unavailable':
      return <AlertCircle className="h-4 w-4 text-gray-500" />;
    case 'error':
      return <AlertCircle className="h-4 w-4 text-red-500" />;
  }
};

const getBackendColor = (backend: string) => {
  switch (backend) {
    case 'tensorrt':
      return 'bg-green-500/10 text-green-600 border-green-500/30';
    case 'onnxruntime':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/30';
    case 'pytorch':
      return 'bg-orange-500/10 text-orange-600 border-orange-500/30';
    case 'python':
      return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30';
    default:
      return 'bg-gray-500/10 text-gray-600 border-gray-500/30';
  }
};

const formatNumber = (num: number) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const GPUInstanceModelRepository = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedModelForDelete, setSelectedModelForDelete] = useState<string>('');
  const [versionHistoryDialogOpen, setVersionHistoryDialogOpen] = useState(false);
  const [selectedModelForHistory, setSelectedModelForHistory] = useState<string>('');

  // Get store state
  const getAdditionalModels = useTritonModelStore((state) => state.getAdditionalModels);
  const isModelDeleted = useTritonModelStore((state) => state.isModelDeleted);

  const baseDetails: TritonInstanceDetails = useMemo(
    () => mockTritonInstanceDetails(instanceId || ''),
    [instanceId]
  );

  // Combine mock models with uploaded models, filter deleted ones
  const allModels: TritonModelInfo[] = useMemo(() => {
    const additionalModels = getAdditionalModels(instanceId || '');
    const uploadedModelInfos = additionalModels.map((m) =>
      uploadedModelToTritonModelInfo(m, instanceId || '')
    );

    // Filter out deleted models from base models
    const filteredBaseModels = baseDetails.models.filter(
      (m) => !isModelDeleted(instanceId || '', m.name)
    );

    return [...filteredBaseModels, ...uploadedModelInfos];
  }, [baseDetails.models, instanceId, getAdditionalModels, isModelDeleted]);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDeleteClick = (modelName: string) => {
    setSelectedModelForDelete(modelName);
    setDeleteDialogOpen(true);
  };

  const handleTestClick = (modelName: string) => {
    navigate(`model-testing?model=${encodeURIComponent(modelName)}`);
  };

  const handleVersionHistoryClick = (modelName: string) => {
    setSelectedModelForHistory(modelName);
    setVersionHistoryDialogOpen(true);
  };

  // Get version count for a model (from mock data)
  const mockVersionHistory = useMemo(
    () => getMockModelVersionHistory(instanceId || ''),
    [instanceId]
  );

  const getVersionCount = (modelName: string): number => {
    const history = mockVersionHistory[modelName];
    return history ? history.versions.length : 1;
  };

  const hasMultipleVersions = (modelName: string): boolean => {
    return getVersionCount(modelName) > 1;
  };

  const { modelManagement } = baseDetails;
  const readyModels = allModels.filter((m) => m.status === 'ready').length;

  return (
    <div className="container py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6" />
            Model Repository
          </h1>
          <p className="text-muted-foreground">
            Deployed models and their inference endpoints
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setUploadDialogOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Upload Model
          </Button>
          <Badge variant="outline" className="font-mono text-xs">
            {modelManagement.controlMode.toUpperCase()} mode
          </Badge>
          <Badge variant="outline" className="bg-green-500/10 text-green-600">
            {readyModels}/{allModels.length} Models Ready
          </Badge>
        </div>
      </div>

      {/* Model Repository Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Deployed Models</CardTitle>
          <CardDescription>
            All models currently deployed to this Triton instance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Model</TableHead>
                <TableHead>Backend</TableHead>
                <TableHead>Instances</TableHead>
                <TableHead>Batching</TableHead>
                <TableHead>Inferences</TableHead>
                <TableHead>Latency</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allModels.map((model) => (
                <TableRow key={model.name}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getModelIcon(model.name, model.backend)}
                      <div>
                        <div className="font-medium">{model.name}</div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <span>v{model.version}</span>
                          {hasMultipleVersions(model.name) && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 px-1.5 py-0 text-xs"
                                    onClick={() => handleVersionHistoryClick(model.name)}
                                  >
                                    <GitBranch className="h-3 w-3 mr-0.5" />
                                    {getVersionCount(model.name)} versions
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>View version history</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn("font-mono text-xs", getBackendColor(model.backend))}>
                      {model.backend}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{model.instances}</TableCell>
                  <TableCell>
                    {model.dynamicBatching ? (
                      <Badge variant="outline" className="text-xs text-green-600 border-green-600/30">
                        max {model.maxBatchSize}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">Off</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {formatNumber(model.metrics.inferenceCount)}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <span className="font-mono">{model.metrics.avgLatencyMs}ms</span>
                      <span className="text-xs text-muted-foreground ml-1">avg</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {getStatusIcon(model.status)}
                      <span className="text-sm capitalize">{model.status}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {model.status === 'ready' && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleTestClick(model.name)}
                              >
                                <Play className="size-4 text-green-600" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Test model</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleVersionHistoryClick(model.name)}
                            >
                              <History className="size-4 text-blue-500" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Version history</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCopy(model.endpoint, model.name)}
                            >
                              {copiedField === model.name ? (
                                <Check className="size-4" />
                              ) : (
                                <Copy className="size-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Copy endpoint</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteClick(model.name)}
                            >
                              <Trash2 className="size-4 text-red-500" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Delete model</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Per-Model Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Per-Model Metrics</CardTitle>
          <CardDescription>Detailed performance metrics for each model</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {allModels.filter(m => m.status === 'ready').map((model) => (
              <div key={model.name} className="p-4 bg-muted/30 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  {getModelIcon(model.name, model.backend)}
                  <span className="font-medium">{model.name}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="p-2 bg-background rounded">
                    <div className="text-xs text-muted-foreground">Avg Latency</div>
                    <div className="font-mono">{model.metrics.avgLatencyMs}ms</div>
                  </div>
                  <div className="p-2 bg-background rounded">
                    <div className="text-xs text-muted-foreground">P99 Latency</div>
                    <div className="font-mono">{model.metrics.p99LatencyMs}ms</div>
                  </div>
                  <div className="p-2 bg-background rounded">
                    <div className="text-xs text-muted-foreground">Queue Time</div>
                    <div className="font-mono">{model.metrics.queueTimeMs}ms</div>
                  </div>
                  <div className="p-2 bg-background rounded">
                    <div className="text-xs text-muted-foreground">Inferences</div>
                    <div className="font-mono">{formatNumber(model.metrics.inferenceCount)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <UploadModelDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        instanceId={instanceId || ''}
      />

      <DeleteModelDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        modelName={selectedModelForDelete}
        instanceId={instanceId || ''}
      />

      <VersionHistoryDialog
        open={versionHistoryDialogOpen}
        onOpenChange={setVersionHistoryDialogOpen}
        modelName={selectedModelForHistory}
        instanceId={instanceId || ''}
      />
    </div>
  );
};

export default GPUInstanceModelRepository;
